import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useParams, useSearch } from 'wouter';
import { FormattedMessage } from 'react-intl';
import queryString from 'query-string';

import { FormPanel, Pagination } from '@micromag/core/components';
import { useFormattedDate } from '@micromag/core/hooks';
import { Toggle } from '@micromag/fields';
import { useUrlGenerator } from '@micromag/core/contexts';
import { PropTypes as MicromagPropTypes } from '@micromag/core';

import useStory from '../../../hooks/useStory';
import useStoryVersions from '../../../hooks/useStoryVersions';
import MainLayout from '../../layouts/Main';
import Page from '../../partials/Page';
import StoryMenu from '../../menus/Story';
import VersionsList from '../../lists/Versions';

const propTypes = {
    itemsPerPage: PropTypes.number,
    className: PropTypes.string,
};

const defaultProps = {
    itemsPerPage: 10,
    className: null,
};

const StoryVersionsPage = ({ itemsPerPage, className }) => {
    const [pathname] = useLocation();
    const search = useSearch();
    const url = useUrlGenerator();
    const [filterPublished, setFilterPublished] = useState();
    const getDate = useFormattedDate();
    const { story: storyId } = useParams();
    const { story } = useStory(storyId);

    const { page: pageString = 1 } = queryString.parse(search);
    const page = parseInt(pageString, 10);

    const query = useMemo(() => ({ order: ['created_at', 'DESC'] }), []);
    const { versions, lastPage, total } = useStoryVersions(storyId, query, page, itemsPerPage);

    const hasStory = story !== null;
    const hasVersions = versions !== null;
    const isLoading = !hasStory || !hasVersions;

    const parentTitle = hasStory ? story.title : null;
    const parentUrl = hasStory ? url('stories.show', { story: story.id }) : null;
    const title = <FormattedMessage defaultMessage="Versions" description="Versions title" />;

    const onFilteredChange = useCallback(() => {
        setFilterPublished((published) => !published);
    }, [setFilterPublished]);

    // filter unpublished versions if toggle is set

    const filteredVersions = useMemo(
        () =>
            filterPublished
                ? versions.filter(
                      ({ publication_version: publicationVersion = null }) =>
                          publicationVersion !== null,
                  )
                : versions,
        [filterPublished, versions],
    );

    // group versions by date

    const versionsByDate = useMemo(
        () =>
            (filteredVersions || []).reduce((acc, version) => {
                const { created_at: createdAt = null } = version;
                const date = getDate(createdAt);
                if (!acc[date]) {
                    acc[date] = [];
                }
                acc[date].push(version);
                return acc;
            }, {}),
        [filteredVersions, getDate],
    );

    const hasData = hasStory && hasVersions && versions.length > 0;
    const hasPublished =
        hasData &&
        versions.find(
            ({ publication_version: publicationVersion = null }) => publicationVersion !== null,
        );

    const paginationElement = (
        <div className="d-flex align-items-center justify-content-end w-100">
            <Pagination
                url={pathname}
                query={query}
                page={page}
                lastPage={lastPage}
                total={total}
            />
        </div>
    );

    return (
        <MainLayout
            nav={[
                { label: parentTitle, url: parentUrl },
                { label: title, url: pathname },
            ]}
        >
            <Page
                title={title}
                sidebar={story !== null ? <StoryMenu story={story} asList /> : <div />}
                className={className}
            >
                <FormPanel loading={isLoading}>
                    {hasPublished ? (
                        <div className="form-group w-100 d-flex align-items-center justify-content-between">
                            <div className="label">
                                <FormattedMessage
                                    defaultMessage="Only show published"
                                    description="Only show published field label"
                                />
                            </div>
                            <Toggle value={filterPublished} onChange={onFilteredChange} />
                        </div>
                    ) : null}
                    {hasData ? (
                        <>
                            {paginationElement}
                            {Object.keys(versionsByDate).map((date) => (
                                <VersionsList
                                    key={`versions-${date}`}
                                    story={story}
                                    title={date}
                                    items={versionsByDate[date]}
                                />
                            ))}
                            {paginationElement}
                        </>
                    ) : (
                        <FormattedMessage
                            defaultMessage="No versions yet"
                            description="No versions yet message"
                        />
                    )}
                </FormPanel>
            </Page>
        </MainLayout>
    );
};

StoryVersionsPage.propTypes = propTypes;
StoryVersionsPage.defaultProps = defaultProps;

export default StoryVersionsPage;
